import i18n, { type BackendModule } from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import { brandConfig, brandLinks } from './shared/config/brand';

const localeLoaders: Record<string, () => Promise<{ default: object }>> = {
  en: () => import('./locales/en/translation.json'),
  ru: () => import('./locales/ru/translation.json'),
  tr: () => import('./locales/tr/translation.json'),
};

type TranslationTree =
  | string
  | number
  | boolean
  | null
  | TranslationTree[]
  | { [key: string]: TranslationTree };

const brandPlaceholders = [
  ['{brandName}', brandConfig.name],
  ['{brandLegalName}', brandConfig.legalName],
  ['{brandInn}', brandConfig.inn],
  ['{brandDomain}', brandConfig.domain],
  ['{site}', brandConfig.siteUrl],
  ['{email}', brandConfig.email],
  ['{tgBotLink}', brandLinks.telegramBot],
  ['{tgOperatorLink}', brandLinks.telegramOperator],
  ['{tgChannelLink}', brandLinks.telegramChannel],
  ['{whatsappLink}', brandLinks.whatsapp],
] as const;

const applyBrandPlaceholders = (value: TranslationTree): TranslationTree => {
  if (typeof value === 'string') {
    return brandPlaceholders.reduce(
      (text, [placeholder, replacement]) => text.replaceAll(placeholder, replacement),
      value,
    );
  }

  if (Array.isArray(value)) {
    return value.map(applyBrandPlaceholders);
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, applyBrandPlaceholders(item)]),
    );
  }

  return value;
};

const localeBackend: BackendModule = {
  type: 'backend',
  init: () => undefined,
  read: (language, _namespace, callback) => {
    const loader = localeLoaders[language] ?? localeLoaders.ru;
    loader()
      .then(({ default: translations }) =>
        callback(null, applyBrandPlaceholders(translations as TranslationTree) as object),
      )
      .catch((error: Error) => callback(error, false));
  },
};

i18n
  .use(localeBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    supportedLngs: ['ru', 'en', 'tr'],
    load: 'languageOnly',
    fallbackLng: 'ru',
    interpolation: {
      escapeValue: false
    }
  });

export default i18n;
